/**
 * Patients API Service
 * Handles all patient-related API calls
 */

import axios from '@/config/axios';
import { i18n } from '@/locales';
import type {
  Patient,
  PatientDetail,
  CreatePatientDto,
  UpdatePatientDto,
  PaginatedPatientsResponse,
  ActivatePatientPortalDto,
  HouseholdMember,
  AddHouseholdMemberDto,
} from '@/types/api-types';

// Drops `""` values and recurses into plain object children. Patient forms
// initialize text inputs to `''`, and class-validator's `@IsOptional()` only
// skips on null/undefined — empty strings still trip `@IsEmail()` etc. Stripping
// here means every create-patient entry point (reception, check-in, registration)
// gets the same lenient behavior without each form needing its own cleanup.
function stripEmptyStrings<T>(value: T): T {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map(stripEmptyStrings) as unknown as T;
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === '') continue;
      out[key] = stripEmptyStrings(v);
    }
    return out as T;
  }
  return value;
}

/** The orders GET /patients sorts by; patients without the value always come last. */
export const PatientListSortField = {
  CreatedAt: 'createdAt',
  LastVisit: 'lastVisit',
  NextAppointment: 'nextAppointment',
  /** Last name, then first name, ignoring case and accents. */
  Name: 'name',
} as const;
export type PatientListSortField = (typeof PatientListSortField)[keyof typeof PatientListSortField];

export const SortOrder = { Asc: 'asc', Desc: 'desc' } as const;
export type SortOrder = (typeof SortOrder)[keyof typeof SortOrder];

export interface ListPatientsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: 'active' | 'inactive';
  hasPortalAccess?: boolean;
  insuranceType?: string;
  minAge?: number;
  maxAge?: number;
  /** YYYY-MM-DD: only patients whose last visit is on or after that day. */
  lastVisitFrom?: string;
  hasUpcomingAppointment?: boolean;
  /** true: only patients with at least one chronic condition. */
  hasChronicConditions?: boolean;
  sortBy?: PatientListSortField;
  /** Defaults to createdAt desc, lastVisit desc, nextAppointment asc, name asc. */
  sortOrder?: SortOrder;
}

// CORA-148/149 — fuzzy duplicate detection. Mirrors the backend
// `FindDuplicatePatientsQueryDto`/response DTOs exactly. Either pass `patientId`
// to score an existing patient against the rest of its org, OR any subset of the
// candidate fields for a pre-create check. The query key for the date of birth
// is `dob` (the controller maps it onto the use case's `dateOfBirth`).
/** Mirrors the backend PortalInvitationResponseDto. */
export interface PortalInvitationResponse {
  sent: boolean;
  /** ISO timestamp — when the activation link stops working. */
  expiresAt: string;
}

export interface FindDuplicatesParams {
  patientId?: string;
  /** Exclude the record being edited so a pre-update check never matches itself. */
  excludeId?: string;
  firstName?: string;
  lastName?: string;
  /** YYYY-MM-DD civil date. */
  dob?: string;
  phone?: string;
  nationalId?: string;
}

// CORA-153 — minimal autocomplete projection. Mirrors the backend
// `PatientAutocompleteItemDto`: `id` is for `:key` / fetch payloads only,
// `patientCode` is the display id (never the UUID — patient-code rule).
export interface PatientAutocompleteItem {
  /** Patient UUID — for :key / fetch payloads, never for display. */
  id: string;
  /** Human-readable patient code — use this for display, never the UUID. */
  patientCode: string;
  firstName: string;
  lastName: string;
  fullName: string;
  /** YYYY-MM-DD civil date (no timezone). */
  dateOfBirth: string;
  phone: string;
}

/** Confidence band returned per candidate — mirrors the backend `DuplicateConfidence` enum. */
export const DUPLICATE_CONFIDENCE = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
} as const;

export type DuplicateConfidence = (typeof DUPLICATE_CONFIDENCE)[keyof typeof DUPLICATE_CONFIDENCE];

export interface DuplicateMatchFlags {
  name: boolean;
  dateOfBirth: boolean;
  phone: boolean;
  nationalId: boolean;
}

export interface DuplicatePatientMatch {
  /** Patient UUID — for :key / fetch / merge payloads, never for display. */
  id: string;
  /** Human-readable patient code — use this for display, never the UUID. */
  patientCode: string;
  firstName: string;
  lastName: string;
  fullName: string;
  /** YYYY-MM-DD civil date (no timezone). */
  dateOfBirth: string;
  age: number;
  /** Normalized combined match score (0–1). */
  score: number;
  confidence: DuplicateConfidence;
  matches: DuplicateMatchFlags;
}

export interface FindDuplicatePatientsResponse {
  matches: DuplicatePatientMatch[];
}

// CORA-150/151 — per-field winner for the conflicting scalars of a merge.
// Mirrors the backend `PatientMergeFieldChoice`: 'primary' keeps the primary
// record's value, 'secondary' takes the duplicate's. Omitted fields keep primary.
export const MERGE_FIELD_CHOICE = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
} as const;

export type MergeFieldChoice = (typeof MERGE_FIELD_CHOICE)[keyof typeof MERGE_FIELD_CHOICE];

// CORA-151 — request body for `POST /patients/:id/merge`. Matches the backend
// `MergePatientsDto` field-for-field: the duplicate's id plus optional per-field
// winner choices for the conflicting scalars (name / DOB / phone / national ID).
export interface PatientMergeFieldResolutions {
  firstName?: MergeFieldChoice;
  lastName?: MergeFieldChoice;
  dateOfBirth?: MergeFieldChoice;
  phone?: MergeFieldChoice;
  nationalId?: MergeFieldChoice;
}

export interface MergePatientsPayload {
  /** The duplicate (secondary) patient UUID to fold into the primary and soft-delete. */
  secondaryPatientId: string;
  fieldResolutions?: PatientMergeFieldResolutions;
}

// CORA-151 — response of a successful merge (backend `PatientMergeResultDto`):
// the post-merge primary patient, the immutable audit row id, and the per-table
// re-parent counts.
export interface PatientMergeResult {
  patient: Patient;
  auditId: string;
  reparentedCounts: Record<string, number>;
}

/**
 * Patients API
 */
export const patientsApi = {
  /**
   * List patients with pagination and filters
   */
  async list(params?: ListPatientsParams): Promise<PaginatedPatientsResponse> {
    const response = await axios.get('/patients', { params });
    return response.data;
  },

  /**
   * Get patient by ID
   */
  async getById(id: string): Promise<PatientDetail> {
    const response = await axios.get(`/patients/${id}`);
    return response.data;
  },

  /**
   * Get the patient record linked to the authenticated user.
   * Only callable as a PATIENT — the backend rejects other roles.
   */
  async getMyProfile(): Promise<Patient> {
    const response = await axios.get('/patients/me');
    return response.data;
  },

  /**
   * Create new patient
   */
  async create(data: CreatePatientDto): Promise<Patient> {
    const response = await axios.post('/patients', stripEmptyStrings(data));
    return response.data;
  },

  /**
   * Update patient
   */
  async update(id: string, data: UpdatePatientDto): Promise<Patient> {
    const response = await axios.put(`/patients/${id}`, stripEmptyStrings(data));
    return response.data;
  },

  /**
   * Delete patient (soft delete)
   */
  async delete(id: string): Promise<void> {
    await axios.delete(`/patients/${id}`);
  },

  /**
   * CORA-153 — patient autocomplete (typeahead). Hits the lightweight
   * `GET /patients/search` endpoint, which matches name / phone / email /
   * national ID / patient code / date of birth (full or partial YYYY-MM-DD) and
   * returns a minimal, org-scoped projection (patientCode for display, never the
   * UUID). `limit` caps the suggestion count.
   */
  async autocomplete(q: string, limit?: number): Promise<PatientAutocompleteItem[]> {
    const response = await axios.get<PatientAutocompleteItem[]>('/patients/search', {
      params: { q, ...(limit !== undefined ? { limit } : {}) },
    });
    return response.data;
  },

  /**
   * Search patients by name, phone, ID, or date of birth — shares the single
   * server-side autocomplete path (CORA-153) so every typeahead surface behaves
   * identically.
   */
  async search(query: string, limit?: number): Promise<PatientAutocompleteItem[]> {
    return this.autocomplete(query, limit);
  },

  // ============================================================================
  // Portal Management
  // ============================================================================

  /**
   * Activate patient portal access
   */
  async activatePortal(id: string, data: ActivatePatientPortalDto): Promise<Patient> {
    const response = await axios.post(`/patients/${id}/activate-portal`, data);
    return response.data;
  },

  /**
   * CORA-154 — email the patient a single-use activation link so they set their
   * own password. Preferred over `activatePortal`, which has staff choosing the
   * patient's password. Server-side this needs a specific active hospital, an
   * email on the record, and the portal not already active; it is also rate
   * limited, so a 429 is a normal outcome to surface rather than retry.
   */
  async sendPortalInvitation(id: string): Promise<PortalInvitationResponse> {
    const response = await axios.post<PortalInvitationResponse>(`/patients/${id}/portal-invitation`, {});
    return response.data;
  },

  /**
   * Resend access for a patient who ALREADY has a portal account: emails a
   * password-reset link. The invitation endpoint refuses these patients, because
   * its activation step creates the user and would fail for an existing one.
   */
  async resendPortalAccess(id: string): Promise<PortalInvitationResponse> {
    const response = await axios.post<PortalInvitationResponse>(`/patients/${id}/portal-access-resend`, {});
    return response.data;
  },

  /**
   * Deactivate patient portal access
   */
  async deactivatePortal(id: string): Promise<Patient> {
    const response = await axios.delete(`/patients/${id}/deactivate-portal`);
    return response.data;
  },

  /**
   * CORA-66: admin override for the no-show auto-block. Clears the block,
   * resets the patient's no-show count, and writes an audit row. The `reason`
   * is required server-side (≥ 5 characters).
   */
  async unblockBooking(id: string, reason: string): Promise<Patient> {
    const response = await axios.post(`/patients/${id}/unblock-booking`, { reason });
    return response.data;
  },

  // ============================================================================
  // Household Members
  // ============================================================================

  /**
   * Add household member
   */
  async addHouseholdMember(patientId: string, data: AddHouseholdMemberDto): Promise<HouseholdMember> {
    const response = await axios.post(`/patients/${patientId}/household`, data);
    return response.data;
  },

  /**
   * List household members
   */
  async listHouseholdMembers(patientId: string): Promise<HouseholdMember[]> {
    const response = await axios.get(`/patients/${patientId}/household`);
    return response.data;
  },

  /**
   * Update household member
   */
  async updateHouseholdMember(patientId: string, memberId: string, data: Partial<AddHouseholdMemberDto>): Promise<HouseholdMember> {
    const response = await axios.put(`/patients/${patientId}/household/${memberId}`, data);
    return response.data;
  },

  /**
   * Remove household member
   */
  async removeHouseholdMember(patientId: string, memberId: string): Promise<void> {
    await axios.delete(`/patients/${patientId}/household/${memberId}`);
  },

  /**
   * CORA-49 — booking suggestions for a patient. Returns recent specialties,
   * doctors with repeat visits, and a recent-visits list. Used by the booking
   * form to surface a "Suggested" group above the doctor / specialty pickers.
   */
  async getBookingSuggestions(patientId: string): Promise<PatientBookingSuggestions> {
    const response = await axios.get<PatientBookingSuggestions>(`/patients/${patientId}/booking-suggestions`);
    return response.data;
  },

  /**
   * CORA-148/149 — fuzzy duplicate detection. Returns ranked candidate matches
   * (highest score first) scored within the caller's organization. Used to warn
   * staff about likely existing records before creating a new patient; it is a
   * soft, non-blocking signal (the create still hard-rejects exact collisions).
   */
  async findDuplicates(params: FindDuplicatesParams): Promise<FindDuplicatePatientsResponse> {
    const response = await axios.get<FindDuplicatePatientsResponse>('/patients/duplicates', { params });
    return response.data;
  },

  /**
   * CORA-150/151 — merge a duplicate (secondary) patient into the primary
   * (`primaryId` = path :id). Folds the secondary's references and collections
   * into the primary, applies the per-field winner choices for conflicting
   * scalars, and soft-deletes the secondary — atomic and IRREVERSIBLE. Admin-only
   * server-side (ADMIN / HOSPITAL_ADMIN / SUPER_ADMIN); rejects self-merge and
   * cross-organization merges.
   */
  async merge(primaryId: string, payload: MergePatientsPayload): Promise<PatientMergeResult> {
    const response = await axios.post<PatientMergeResult>(`/patients/${primaryId}/merge`, payload);
    return response.data;
  },

  /**
   * CORA-152 — fetch the printable patient ID wristband (Code128 barcode of the
   * patient code) as a PDF Blob, so the caller can open or download it without
   * leaving the page. Accept-Language picks the render locale for the static
   * labels (mirrors the receipt download).
   */
  async getBracelet(id: string): Promise<Blob> {
    const locale = i18n.global.locale.value;
    const response = await axios.get<Blob>(`/patients/${id}/bracelet`, {
      responseType: 'blob',
      headers: { 'Accept-Language': locale },
    });
    return response.data;
  },

  // ───────────────────────────────────────────────────────────────────────────
  // CORA-312 — GDPR archive export + two-step erasure. SUPER_ADMIN/ADMIN only
  // (backend @RequiredRoles; the UI additionally hides the actions).
  // ───────────────────────────────────────────────────────────────────────────

  /** Generate the GDPR archive; returns a 15-minute download URL or the inline archive (local dev). */
  async gdprExport(patientId: string): Promise<GdprExportResponse> {
    const response = await axios.get<GdprExportResponse>(`/patients/${patientId}/gdpr/export`);
    return response.data;
  },

  /** Step 1 of erasure — records the request; nothing is changed yet. 409 when erased/pending. */
  async requestGdprErasure(patientId: string): Promise<PatientErasureRequestResponse> {
    const response = await axios.post<PatientErasureRequestResponse>(`/patients/${patientId}/gdpr/erasure-request`);
    return response.data;
  },

  /** Step 2 of erasure — executes the anonymization. 409 replay/expired, 404 mismatch. */
  async confirmGdprErasure(patientId: string, requestId: string): Promise<PatientErasureRequestResponse> {
    const response = await axios.post<PatientErasureRequestResponse>(`/patients/${patientId}/gdpr/erasure-confirm`, { requestId });
    return response.data;
  },
};

// CORA-312 — mirrors the backend GdprExportResponseDto / PatientErasureRequestResponseDto.
export interface GdprExportResponse {
  patientCode: string;
  filename: string;
  sizeBytes: number;
  generatedAt: string;
  downloadUrl: string | null;
  downloadUrlExpiresAt: string | null;
  archive: Record<string, unknown> | null;
}

export type ErasureRequestStatus = 'PENDING' | 'CONFIRMED' | 'EXPIRED';

export interface PatientErasureRequestResponse {
  requestId: string;
  patientCode: string;
  status: ErasureRequestStatus;
  requestedByUserId: string | null;
  confirmedByUserId: string | null;
  requestedAt: string;
  expiresAt: string;
  confirmedAt: string | null;
}

export interface BookingSuggestionVisit {
  appointmentId: string;
  date: string;
  time: string;
  specialty: string;
  doctorId: string | null;
  doctorName: string;
  status: string;
}

export interface BookingSuggestionDoctor {
  doctorId: string;
  doctorName: string;
  specialty: string;
  visitCount: number;
  lastVisitDate: string;
}

export interface PatientBookingSuggestions {
  recentSpecialties: string[];
  repeatVisitDoctors: BookingSuggestionDoctor[];
  lastVisits: BookingSuggestionVisit[];
}

export default patientsApi;
